import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchInvoiceById, addInvoiceNote, uploadInvoiceDocument } from '../../store/invoiceSlice';
import { RootState, AppDispatch } from '../../store';
import {
  ArrowLeftIcon,
  PencilIcon,
  DocumentTextIcon,
  PlusIcon,
  DocumentArrowUpIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';

const InvoiceDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { currentInvoice, loading, error } = useSelector((state: RootState) => state.invoices);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(fetchInvoiceById(id));
    }
  }, [dispatch, id]);

  const handleAddNote = async () => {
    if (id && noteText.trim()) {
      await dispatch(addInvoiceNote({ id, text: noteText.trim() }));
      setNoteText('');
      setShowNoteModal(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && id) {
      setUploadingFile(true);
      try {
        await dispatch(uploadInvoiceDocument({ id, file }));
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } finally {
        setUploadingFile(false);
      }
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-800',
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      paid: 'bg-blue-100 text-blue-800',
    };
    return colors[status?.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  if (loading || !currentInvoice) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/invoices')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Invoices
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{currentInvoice.invoiceNumber}</h1>
            <p className="mt-1 text-sm text-gray-600">
              Created on {new Date(currentInvoice.createdAt).toLocaleDateString()}
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => navigate(`/invoices/${id}/edit`)}
              className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              <PencilIcon className="h-5 w-5 mr-2" />
              Edit Invoice
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Invoice Information */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Invoice Information</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Invoice Number</p>
                <p className="mt-1 text-sm font-medium text-gray-900">
                  {currentInvoice.invoiceNumber}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <p className="mt-1">
                  <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(currentInvoice.status)}`}>
                    {currentInvoice.status?.toUpperCase()}
                  </span>
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Issue Date</p>
                <p className="mt-1 text-sm font-medium text-gray-900">
                  {currentInvoice.issueDate ? new Date(currentInvoice.issueDate).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Due Date</p>
                <p className="mt-1 text-sm font-medium text-gray-900">
                  {currentInvoice.dueDate ? new Date(currentInvoice.dueDate).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Related Purchase Order</p>
                <p className="mt-1 text-sm font-medium">
                  <Link
                    to={`/purchase-orders/${currentInvoice.poId?._id}`}
                    className="text-indigo-600 hover:text-indigo-800"
                  >
                    {currentInvoice.poId?.poNumber || 'N/A'}
                  </Link>
                </p>
              </div>
            </div>
          </div>

          {/* Company Information */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Company Information</h2>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Buyer</h3>
                <p className="text-sm text-gray-900">{currentInvoice.buyerId?.name || 'N/A'}</p>
                <p className="text-sm text-gray-500">{currentInvoice.buyerId?.email || ''}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Supplier</h3>
                <p className="text-sm text-gray-900">{currentInvoice.supplierId?.name || 'N/A'}</p>
                <p className="text-sm text-gray-500">{currentInvoice.supplierId?.email || ''}</p>
              </div>
            </div>
          </div>

          {/* Amount Details */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Amount Breakdown</h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Amount</span>
                <span className="text-sm font-medium text-gray-900">
                  ${currentInvoice.amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Tax Amount</span>
                <span className="text-sm font-medium text-gray-900">
                  ${currentInvoice.taxAmount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between pt-3 border-t border-gray-200">
                <span className="text-base font-semibold text-gray-900">Total Amount</span>
                <span className="text-base font-bold text-gray-900">
                  ${currentInvoice.totalAmount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </div>

          {/* Notes Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2" />
                Notes & Comments
              </h2>
              <button
                onClick={() => setShowNoteModal(true)}
                className="inline-flex items-center px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Note
              </button>
            </div>

            {currentInvoice.notes && currentInvoice.notes.length > 0 ? (
              <div className="space-y-3">
                {currentInvoice.notes.map((note, index) => (
                  <div key={index} className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-900">{note.text}</p>
                    <p className="mt-1 text-xs text-gray-500">
                      {new Date(note.createdAt).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No notes added yet</p>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Documents Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <DocumentTextIcon className="h-5 w-5 mr-2" />
                Documents
              </h2>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingFile}
                className="inline-flex items-center px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                <DocumentArrowUpIcon className="h-4 w-4 mr-1" />
                {uploadingFile ? 'Uploading...' : 'Upload'}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileUpload}
                className="hidden"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              />
            </div>

            {currentInvoice.documents && currentInvoice.documents.length > 0 ? (
              <div className="space-y-2">
                {currentInvoice.documents.map((doc, index) => (
                  <div key={index} className="flex items-center p-2 bg-gray-50 rounded-lg">
                    <DocumentTextIcon className="h-5 w-5 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-900 flex-1 truncate">{doc}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No documents uploaded</p>
            )}
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
            <div className="space-y-2">
              <button
                onClick={() => navigate(`/invoices/${id}/edit`)}
                className="w-full flex items-center px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 rounded-lg"
              >
                <PencilIcon className="h-5 w-5 mr-3 text-gray-400" />
                Edit Invoice
              </button>
              <Link
                to={`/purchase-orders/${currentInvoice.poId?._id}`}
                className="w-full flex items-center px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 rounded-lg"
              >
                <DocumentTextIcon className="h-5 w-5 mr-3 text-gray-400" />
                View Related PO
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Add Note Modal */}
      {showNoteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Note</h3>
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Enter your note..."
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            />
            <div className="flex justify-end space-x-3 mt-4">
              <button
                onClick={() => {
                  setShowNoteModal(false);
                  setNoteText('');
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddNote}
                disabled={!noteText.trim() || loading}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                Add Note
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceDetails;
